import deviceQuery from "./graphql/device_query.graphql";
import { runQuery } from "@/api/query";
import devicesQuery from "@/api/graphql/devices_query.graphql";
import devicesIdsQuery from "@/api/graphql/devices_ids_query.graphql";
import deviceUpdateOwnerMutation from "@/api/graphql/device_update_owner.graphql";
import deviceEventsQuery from "@/api/graphql/device_events_query.graphql";
import deviceUptimeQuery from "@/api/graphql/device_uptime_query.graphql";
import deviceUptimeHistoryQuery from "@/api/graphql/device_uptime_history_query.graphql";
import devicePresenceQuery from "@/api/graphql/device_presence_query.graphql";
import aggregationQuery from "@/api/graphql/aggregation_query.graphql";
import deviceConfigUpdate from "@/api/graphql/device_config_update.graphql";
import configTemplateQuery from "@/api/graphql/config_template_query.graphql";
import configTemplateCreate from "@/api/graphql/config_template_create.graphql";
import configTemplateUpdate from "@/api/graphql/config_template_update.graphql";
import configTemplateDelete from "@/api/graphql/config_template_delete.graphql";
import applyConfigTemplateMutation from "@/api/graphql/apply_config_template.graphql";
import createSprinterJobMutation from "@/api/graphql/sprinter_job_create.graphql";

export const queryDevices = async (nextPageToken = null, filter = null, take = 50) => {
  const result = await runQuery(devicesQuery, { nextPageToken, take: take, filter });

  let devices = result ? result.devices.edges.map((edge) => edge.node) : [];
  const devicesAggregation = result ? result.devices.aggregation : {};
  const hasNextPage = result ? result.devices.pageInfo.hasNextPage : false;
  const newNextPageToken = result ? result.devices.pageInfo.endCursor : "";

  devices = devices.map((device) => {
    if (device.configV2 && device.configV2.data && typeof device.configV2.data == "string") {
      return {
        ...device,
        configV2: JSON.parse(device.configV2.data),
        stateAlarms:
          device.stateAlarms && device.configV2.data && typeof device.configV2.data == "string"
            ? JSON.parse(device.stateAlarms.data)
            : device.stateAlarms,
      };
    }
    return device;
  });

  return {
    devices,
    hasNextPage,
    nextPageToken: newNextPageToken,
    aggregation: devicesAggregation,
  };
};

export const queryDevice = async (serialNumber) => {
  const result = await runQuery(deviceQuery, { serialNumber });

  if (!result || !result.devices.edges || result.devices.edges.length === 0) {
    return null;
  }

  const device = result.devices.edges[0].node;
  device.configV2 = device.configV2.data ? JSON.parse(device.configV2.data) : {};
  device.stateAlarms = device.stateAlarms.data ? JSON.parse(device.stateAlarms.data) : {};

  return device;
};

export const queryDeviceIds = async () => {
  const result = await runQuery(devicesIdsQuery);
  return result ? result.devices.edges.map((edge) => edge.node) : [];
};

export const queryAggregations = async () => {
  return await runQuery(aggregationQuery);
};

export const updateOwner = async (deviceIds, tenantId) => {
  return runQuery(deviceUpdateOwnerMutation, {
    deviceIds,
    tenantId,
  });
};

export const queryDeviceEvents = async (deviceId, nextPageToken = null, take = 20, eventTypes = null) => {
  const result = await runQuery(
    deviceEventsQuery,
    {
      deviceId,
      nextPageToken,
      take,
      eventTypes: eventTypes && eventTypes.length > 0 ? eventTypes : [],
    },
    { silent: true }
  );

  if (!result) return null;

  if (!result.devices || !result.devices.edges || result.devices.edges.length === 0) {
    return { events: [], hasNextPage: false, nextPageToken: null };
  }

  const device = result.devices.edges[0].node;

  return {
    events: device.events.edges,
    hasNextPage: device.events.pageInfo.hasNextPage,
    nextPageToken: device.events.pageInfo.endCursor,
  };
};

const firstDeviceUptime = (result) => {
  if (!result || !result.devices || !result.devices.edges || result.devices.edges.length === 0) {
    return null;
  }

  return result.devices.edges[0].node.uptime;
};

// Silent: the uptime views show their empty state instead of an error notification.
export const queryDeviceUptime = async (serialNumber, start, end) => {
  return firstDeviceUptime(await runQuery(deviceUptimeQuery, { serialNumber, start, end }, { silent: true }));
};

export const queryDeviceUptimeHistory = async (deviceId, start, end) => {
  return firstDeviceUptime(await runQuery(deviceUptimeHistoryQuery, { deviceId, start, end }, { silent: true }));
};

// Super admins only; silent, so the expert view shows its own error state.
export const queryDevicePresence = async (deviceId, limit) => {
  const result = await runQuery(devicePresenceQuery, { deviceId, limit }, { silent: true });

  if (!result || !result.devices || !result.devices.edges || result.devices.edges.length === 0) {
    return null;
  }

  return result.devices.edges[0].node.presence;
};

export const updateConfigV2 = async (deviceId, config) => {
  return await runQuery(deviceConfigUpdate, {
    id: deviceId,
    data: JSON.stringify(config),
  });
};

export const applyConfigTemplate = async (templateId, devices, dryRun = false) => {
  return await runQuery(applyConfigTemplateMutation, {
    input: {
      templateId: templateId,
      devices: devices.map(({ deviceId, values }) => ({
        deviceId: deviceId,
        values: JSON.stringify(values),
      })),
      dryRun: dryRun,
    },
  });
};

export const createConfigTemplate = async (template) => {
  return await runQuery(configTemplateCreate, {
    name: template.name,
    ownerId: template.ownerId,
    applierIds: template.applierIds,
    template: template.template,
  });
};

export const updateConfigTemplate = async (template) => {
  return await runQuery(configTemplateUpdate, {
    templateId: template.id,
    name: template.name,
    applierIds: template.applierIds,
    template: template.template,
  });
};

export const deleteConfigTemplate = async (templateId) => {
  return await runQuery(configTemplateDelete, { templateId: templateId });
};

export const queryConfigTemplates = async (applierId, nextPageToken = null, pageSize) => {
  const result = await runQuery(configTemplateQuery, { applierId, nextPageToken, take: pageSize });
  const templates = result ? result.templates.edges.map((edge) => edge.node) : [];

  const newNextPageToken = result ? result.templates.pageInfo.endCursor : "";
  const hasNextPage = result ? result.templates.pageInfo.hasNextPage : false;

  return {
    templates,
    hasNextPage,
    nextPageToken: newNextPageToken,
  };
};

export const createSprinterJob = async (things) => {
  return await runQuery(createSprinterJobMutation, {
    things,
  });
};
